'use client'

import { useState, useEffect } from 'react'
import { Loader2 } from 'lucide-react'

import CrawlResults from './components/CrawlResults'
import TeaserResult, {
  type TeaserReport,
} from '@/components/tools/TeaserResult'
import { useToolUsage } from '@/lib/use-tool-usage'
import { useToolAccessGate } from '@/components/tools/ToolAccessGate'
import { UsageLimitGate } from '@/components/tools/UsageLimitGate'

import { CRAWL_BOTS } from '@/lib/tools/crawl-bots'
import ToolAiCrawlCheckerRef from '@/components/reference/ToolAiCrawlCheckerRef'
import { useUrlRun } from '@/components/reference/tool-run'
import '@/components/reference/tool-page.css'
import '@/components/reference/tool-live.css'

// Matches API response shape
interface CrawlCheckResult {
  url: string
  checkedAt: string
  /** null when the grade was withheld (scan too blocked to grade) */
  overallScore: number | null
  grade: 'A' | 'B' | 'C' | 'D' | 'F' | null
  scoreStatus?: 'graded' | 'withheld'
  scanCompleteness: number
  pageBlocked: boolean
  botAccess: {
    bot: string
    category: 'search' | 'ai-browse' | 'ai-train' | 'seo'
    allowed: boolean
    robotsTxt: 'allowed' | 'blocked' | 'no-rule'
    httpStatus: number
    responseTime: number
    xRobotsTag: string | null
  }[]
  robotsTxt: {
    exists: boolean
    blocked: boolean
    accessFailed?: boolean
    hasSitemap: boolean
    hasCrawlDelay: boolean
    rawContent: string
  }
  metaRobots: {
    global: string | null
    botSpecific: { bot: string; content: string }[]
  }
  structuredData: {
    found: boolean
    types: string[]
    count: number
    hasArticle: boolean
    hasFaq: boolean
    hasOrganization: boolean
    hasProduct: boolean
    hasBreadcrumb: boolean
  }
  llmsTxt: {
    exists: boolean
    blocked: boolean
    accessFailed?: boolean
    score: number
    hasSections: boolean
    hasEntityDefinitions: boolean
    hasUrls: boolean
    hasUsePolicy: boolean
    wordCount: number
  }
  contentAccess: {
    hasContent: boolean
    titleLength: number
    descriptionLength: number
    framework: string | null
    isServerRendered: boolean
  }
  recommendations: {
    priority: 'high' | 'medium' | 'low'
    category: string
    message: string
  }[]
  aiSummary: {
    summary: string
    actions: string[]
    context: string
  } | null
}

const LOADING_STEPS = [
  'Fetching robots.txt...',
  'Testing bot access...',
  'Checking structured data...',
  'Detecting llms.txt...',
  'Analyzing content...',
  'Generating AI summary...',
]

const CAT_COLORS: Record<string, string> = {
  search: '#008dcc',
  'ai-browse': '#8b5cf6',
  'ai-train': '#F90B8A',
  seo: '#FDC304',
}

// Skeleton loader for results
function ResultsSkeleton() {
  const [stepIndex, setStepIndex] = useState(0)

  useEffect(() => {
    const interval = setInterval(() => {
      setStepIndex(prev => (prev < LOADING_STEPS.length - 1 ? prev + 1 : prev))
    }, 2500)
    return () => clearInterval(interval)
  }, [])

  const progress = Math.round(((stepIndex + 1) / LOADING_STEPS.length) * 100)
  // How many bots have "loaded" (tied to step 1)
  const botsLoaded =
    stepIndex >= 1
      ? Math.min(
          CRAWL_BOTS.length,
          Math.round((stepIndex / 2) * CRAWL_BOTS.length)
        )
      : 0

  return (
    <div className='space-y-6'>
      {/* Progress indicator */}
      <div className='bg-muted/50 rounded-2xl border border-line-strong p-6'>
        <div className='flex flex-col items-center gap-5 py-4'>
          {/* Animated score ring */}
          <div className='relative w-36 h-36'>
            <svg viewBox='0 0 120 120' className='w-full h-full -rotate-90'>
              <circle
                cx='60'
                cy='60'
                r='52'
                fill='none'
                stroke='var(--border)'
                strokeWidth='8'
                opacity={0.3}
              />
              <circle
                cx='60'
                cy='60'
                r='52'
                fill='none'
                stroke='var(--primary)'
                strokeWidth='8'
                strokeLinecap='round'
                strokeDasharray={`${2 * Math.PI * 52}`}
                strokeDashoffset={`${2 * Math.PI * 52 * (1 - progress / 100)}`}
                opacity={0.4}
                className='transition-all duration-1000 ease-out'
              />
            </svg>
            <div className='absolute inset-0 flex flex-col items-center justify-center'>
              <Loader2
                size={24}
                className='animate-spin text-muted-foreground mb-1'
              />
              <span className='text-xs font-medium text-muted-foreground'>
                {progress}%
              </span>
            </div>
          </div>

          {/* Step indicator */}
          <div className='text-center'>
            <p className='text-sm font-medium text-foreground mb-3'>
              {LOADING_STEPS[stepIndex]}
            </p>
            <div className='flex items-center justify-center gap-1.5'>
              {LOADING_STEPS.map((_, i) => (
                <div
                  key={i}
                  className='h-1.5 rounded-full transition-all duration-500'
                  style={{
                    width: i === stepIndex ? '24px' : '6px',
                    backgroundColor:
                      i <= stepIndex ? 'var(--primary)' : 'var(--border)',
                    opacity: i <= stepIndex ? 1 : 0.3,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bot access grid skeleton */}
      <div className='bg-muted/50 rounded-2xl border border-line-strong p-6'>
        <div className='skeleton h-5 w-40 rounded mb-4' />
        <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3'>
          {CRAWL_BOTS.map((bot, i) => {
            const loaded = i < botsLoaded
            const catColor = CAT_COLORS[bot.category]
            return (
              <div
                key={bot.name}
                className='bg-background border border-line-strong rounded-xl px-4 py-3 flex items-center justify-between'
                style={{
                  opacity: loaded ? 1 : 0.35,
                  transition: 'opacity 0.4s ease',
                }}
              >
                <div className='flex items-center gap-2'>
                  <span
                    className='h-2 w-2 rounded-full'
                    style={{
                      backgroundColor: catColor,
                      opacity: loaded ? 1 : 0.3,
                    }}
                  />
                  <span className='text-xs font-medium text-foreground/80'>
                    {bot.name}
                  </span>
                </div>
                {loaded ? (
                  <div className='skeleton h-4 w-12 rounded-full' />
                ) : stepIndex >= 1 && i === botsLoaded ? (
                  <Loader2
                    size={12}
                    className='animate-spin text-muted-foreground'
                  />
                ) : (
                  <div className='h-4 w-12' />
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Score cards skeleton */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        {[
          { label: 'Bot Access & robots.txt', color: '#008dcc', pts: '40 pts' },
          { label: 'Structured Data', color: '#8b5cf6', pts: '25 pts' },
          { label: 'llms.txt', color: '#FDC304', pts: '20 pts' },
          { label: 'Content Quality', color: '#22c55e', pts: '15 pts' },
        ].map((cat, i) => {
          // Each card lights up at its corresponding step (0=bot, 2=sd, 3=llms, 4=content)
          const stepMap = [0, 2, 3, 4]
          const active = stepIndex >= stepMap[i]
          return (
            <div
              key={cat.label}
              className='bg-muted/50 rounded-2xl border border-line-strong p-5'
              style={{
                opacity: active ? 1 : 0.3,
                transition: 'opacity 0.5s ease',
              }}
            >
              <div className='flex items-center justify-between mb-3'>
                <div className='flex items-center gap-2'>
                  <span
                    className='h-2 w-2 rounded-full'
                    style={{ backgroundColor: cat.color }}
                  />
                  <span className='text-sm font-semibold text-foreground/70'>
                    {cat.label}
                  </span>
                </div>
                <span
                  className='text-[10px] font-bold rounded-full px-2 py-0.5'
                  style={{
                    backgroundColor: `${cat.color}15`,
                    color: cat.color,
                    opacity: active ? 1 : 0.3,
                  }}
                >
                  {cat.pts}
                </span>
              </div>
              <div className='skeleton h-2 w-full rounded-full mb-3' />
              <div className='space-y-1.5'>
                <div
                  className='skeleton h-3 rounded'
                  style={{ width: '70%' }}
                />
                <div
                  className='skeleton h-3 rounded'
                  style={{ width: '50%' }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default function CrawlCheckerClient() {
  const [result, setResult] = useState<CrawlCheckResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const toolUsage = useToolUsage('crawl-check')
  const gate = useToolAccessGate('crawl-check', 'AI Crawl Checker', {
    variant: 'reference',
  })

  async function handleSubmit(url: string) {
    if (!gate.ready) {
      // Resumes after the code verifies, so the typed URL runs (PIX-47).
      gate.openGate(url, () => void runCheck(url))
      return
    }

    await runCheck(url)
  }

  async function runCheck(url: string) {
    setIsLoading(true)
    setError(null)
    setResult(null)

    try {
      const response = await toolUsage.fetchWithUsage(
        '/api/tools/crawl-check',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url }),
        }
      )

      const data = await response.json()

      if (
        response.status === 403 &&
        (data.error === 'usage_limit' || data.error === 'gate_required')
      ) {
        gate.openGate(url, () => void runCheck(url))
        return
      }

      if (response.status === 429 && data.error === 'domain_cap') {
        gate.showDomainCap(data)
        return
      }

      if (!response.ok) {
        throw new Error(data.message || 'Analysis failed. Please try again.')
      }

      setResult(data as CrawlCheckResult)
      gate.markConsumed()

      // Scroll to results
      setTimeout(() => {
        document
          .getElementById('crawl-results')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Something went wrong. Please try again.'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const run = useUrlRun({
    example: 'pixelmojo.io',
    isLoading: isLoading,
    error: error,
    onSubmit: handleSubmit,
  })

  return (
    <div className='shell pg-tool pg-tool-ai-crawl-checker'>
      {gate.modal}
      <ToolAiCrawlCheckerRef
        run={run}
        output={
          <>
            <div id='crawl-results' className='max-w-4xl mx-auto'>
              {isLoading && <ResultsSkeleton />}
              {toolUsage.isGated && !isLoading && (
                <UsageLimitGate
                  variant='reference'
                  toolName='AI Crawl Checker'
                  used={toolUsage.used}
                  limit={toolUsage.limit}
                />
              )}
              {result && !isLoading && !toolUsage.isGated && (
                <>
                  {(result as unknown as { access?: { tier?: string } }).access
                    ?.tier === 'teaser' ? (
                    <TeaserResult
                      variant='reference'
                      report={
                        (result as unknown as { report: TeaserReport }).report
                      }
                      toolName='AI Crawl Checker'
                    />
                  ) : (
                    <CrawlResults result={result} />
                  )}
                </>
              )}
            </div>
          </>
        }
      />
    </div>
  )
}
